import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  Activity, LayoutDashboard, Wallet, ArrowRightLeft, 
  Route as RouteIcon, LogOut, User, Menu, X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Eye, FolderOpen } from "lucide-react";

const navItems = [
  { path: "/admin", label: "Обзор", icon: LayoutDashboard },
  { path: "/admin/addresses", label: "Адреса", icon: Wallet },
  { path: "/admin/transactions", label: "Транзакции", icon: ArrowRightLeft },
  { path: "/admin/routes", label: "Маршруты", icon: RouteIcon },
  { path: "/admin/watchlist", label: "Watchlist", icon: Eye },
  { path: "/admin/archives", label: "Архивы", icon: FolderOpen },
];

const AdminLayout = ({ children, user }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/admin/login", { replace: true });
      toast.success("Вы вышли из системы");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/admin/login", { replace: true });
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white flex" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-slate-900">ChainScope</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-slate-100"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 sidebar flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-slate-200 hidden lg:block">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">ChainScope</h1>
              <p className="text-xs text-slate-500">Admin Panel</p>
            </div>
          </Link>
        </div>
        
        <div className="p-4 lg:hidden border-b border-slate-200 mt-14">
          <p className="text-xs text-slate-500">Меню</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${item.path.split("/").pop() || "dashboard"}`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-400 text-center">
            Admin Panel v1.0
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-0 pt-14 lg:pt-0">
        {/* Top bar */}
        <header className="h-14 lg:h-16 border-b border-slate-200 px-4 lg:px-6 flex items-center justify-between bg-white">
          <h2 className="text-base lg:text-lg font-semibold text-slate-900">
            {navItems.find(item => item.path === location.pathname)?.label || "Панель управления"}
          </h2>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-slate-200 text-slate-600">
                    {user?.name?.charAt(0) || "A"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-slate-700 hidden md:block">
                  {user?.name || "Admin"}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-medium">{user?.name}</span>
                  <span className="text-xs text-slate-500">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-rose-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Выйти
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 bg-slate-50 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
